<template>
  <div class="teacher-header" :class="{ 'has-school-logo': hasSchoolLogo }" @wheel.prevent>
    <div class="header-left">
      <div class="logo">
        <img :src="schoolLogo" class="logo-img" />
      </div>
    </div>
    
    <div class="header-center">
      <el-menu
        mode="horizontal"
        :default-active="activeIndex"
        class="header-menu"
        @select="handleSelect"
        background-color="#307CF4"
        text-color="#fff"
        active-text-color="#fff"
        :ellipsis="false"
      >
        <el-menu-item index="home">首页</el-menu-item>
        <el-menu-item v-if="showMenuItem('course-management')" index="course-management">课程管理</el-menu-item>
        <el-menu-item v-if="showMenuItem('question-management')" index="question-management">试题管理</el-menu-item>
        <el-menu-item v-if="showMenuItem('study-circle')" index="study-circle">乐学圈</el-menu-item>
        <el-menu-item v-if="showMenuItem('student-database')" index="student-database">学生信息库</el-menu-item>
      </el-menu>
    </div>
    
    <div class="header-right">
      <el-dropdown @command="handleCommand">
        <span class="user-info">
          <img :src="headerUser.avatar || defaultAvatar" alt="用户头像" class="user-avatar" @error="handleAvatarError">
          <span class="username">{{ isHeaderDataReady ? headerUser.name : '教师用户' }}</span>
          <el-icon><ArrowDown /></el-icon>
        </span>
        <template #dropdown>
          <el-dropdown-menu>
            <el-dropdown-item command="logout">退出登录</el-dropdown-item>
          </el-dropdown-menu>
        </template>
      </el-dropdown>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick, watch } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import { ArrowDown } from '@element-plus/icons-vue'
import { removeToken } from '@/utils/auth'
import { teacherLogout } from '@/api/login'
import { useSchoolStore } from '@/stores/school'
import { useUserStore } from '@/stores/user'
const defaultAvatar = '/teacher/default-user.png'

const router = useRouter()
const schoolStore = useSchoolStore()
const userStore = useUserStore()

const indexRouteNameMap = {
  'course-management': 'CourseManagement',
  'question-management': 'QuestionManagement',
  'study-circle': 'HappyCircle',
  'student-database': 'StudentDatabase'
}

const showMenuItem = (index) => {
  const name = indexRouteNameMap[index]
  if (!name) return true
  return userStore.hasRouteAccess(name)
}

// 根据当前路由设置活跃菜单项
const getActiveIndex = () => {
  const path = router.currentRoute.value.path
  if (path === '/teacher' || path === '/teacher/' || path === '/teacher/home') return 'home'
  if (path.includes('/teacher/content/course-management') && showMenuItem('course-management')) return 'course-management'
  if (path.includes('/teacher/content/question-management') && showMenuItem('question-management')) return 'question-management'
  if (path.includes('/teacher/content/happy-circle') && showMenuItem('study-circle')) return 'study-circle'
  if (path.includes('/teacher/collegesAndClasses/student-database') && showMenuItem('student-database')) return 'student-database'
  return 'home'
}

const activeIndex = ref(getActiveIndex())

// 使用 Pinia 的用户状态提供响应式信息与清理能力
const userInfo = computed(() => {
  const info = userStore.getUserInfo
  return {
    name: info?.name || '教师用户',
    avatar: info?.avatar || '',
    id: info?.id || null,
    role: info?.role || 'teacher'
  }
})

// 标题栏局部显示状态与清理方法
const headerUser = ref({ name: '教师用户', avatar: '', id: null, role: 'teacher' })
const isHeaderDataReady = ref(false)

const clearHeaderUserData = () => {
  headerUser.value = { name: '教师用户', avatar: '', id: null, role: 'teacher' }
  isHeaderDataReady.value = false
}

// 同步 Pinia 的用户信息到标题栏显示状态
watch(() => userStore.getUserInfo, (newInfo) => {
  if (newInfo && Object.keys(newInfo).length) {
    headerUser.value = {
      name: newInfo.name || '教师用户',
      avatar: newInfo.avatar || '',
      id: newInfo.id || null,
      role: newInfo.role || 'teacher'
    }
    isHeaderDataReady.value = true
  } else {
    clearHeaderUserData()
  }
}, { immediate: true, deep: true })

// 自动退出事件处理器（来自 Pinia store 的 window 事件）
const timeoutHandler = () => clearHeaderUserData()

// 学校logo
const schoolLogo = computed(() => {
  const currentSchool = schoolStore.getCurrentSchool
  if (currentSchool && currentSchool.logo) {
    return currentSchool.logo
  }
  // 如果没有学校logo，返回默认图片
  return '/teacher/logo.jpg'
})

// 是否有学校logo
const hasSchoolLogo = computed(() => {
  const currentSchool = schoolStore.getCurrentSchool
  return currentSchool && currentSchool.logo
})

// 头像加载错误处理
const handleAvatarError = (event) => {
  console.warn('头像加载失败:', userInfo.value.avatar)
  // 当头像加载失败时，使用默认头像
  event.target.src = defaultAvatar
}

// 菜单选择处理
const handleSelect = (key) => {
  activeIndex.value = key
  switch (key) {
    case 'home':
      router.push('/teacher')
      break
    case 'course-management':
      router.push('/teacher/content/course-management')
      break
    case 'question-management':
      router.push('/teacher/content/question-management')
      break
    case 'study-circle':
      router.push('/teacher/content/happy-circle')
      break
    case 'student-database':
      router.push('/teacher/collegesAndClasses/student-database')
      break
  }
}

// 下拉菜单处理
const handleCommand = (command) => {
  switch (command) {
    case 'profile':
      router.push('/teacher/profile')
      break
    case 'settings':
      router.push('/teacher/settings')
      break
    case 'logout':
      handleLogout()
      break
  }
}

// 强制刷新菜单显示
const forceMenuRefresh = () => {
  nextTick(() => {
    // 触发Element Plus菜单重新计算
    window.dispatchEvent(new Event('resize'))
  })
}

// 窗口大小变化监听
let resizeTimer = null
const handleResize = () => {
  // 使用防抖避免频繁触发
  if (resizeTimer) {
    clearTimeout(resizeTimer)
  }
  resizeTimer = setTimeout(() => {
    forceMenuRefresh()
  }, 200)
}

// 监听路由变化，更新活跃菜单项
watch(() => router.currentRoute.value.path, () => {
  activeIndex.value = getActiveIndex()
})

// 组件挂载时添加监听器
onMounted(() => {
  window.addEventListener('resize', handleResize)
  // 确保初始状态正确
  activeIndex.value = getActiveIndex()
  // 监听自动退出事件，及时清理标题栏数据
  window.addEventListener('userTimeout', timeoutHandler)
})

// 组件卸载时移除监听器
onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
  if (resizeTimer) {
    clearTimeout(resizeTimer)
  }
  window.removeEventListener('userTimeout', timeoutHandler)
})

// 退出登录
const handleLogout = async () => {
  try {
    await ElMessageBox.confirm('确定要退出登录吗？', '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    })
    
    try {
      // 调用退出登录API
      await teacherLogout()
    } catch (apiError) {
      console.warn('退出登录API调用失败，继续本地清理:', apiError)
    }
    
    // 使用 Pinia 统一清理并确保标题栏数据清空
    try {
      userStore.logout()
    } finally {
      clearHeaderUserData()
    }
    
    ElMessage.success('退出登录成功')
    router.replace({ path: '/teacher/login', query: { reason: 'logout', _t: String(Date.now()) } })
  } catch {
    // 用户取消退出
  }
}
</script>

<style scoped>
.teacher-header {
  height: 80px;
  background: #307CF4;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  padding-bottom: 20px;
  z-index: 1;
  min-width: 1300px;
  overflow-x: auto;
  overflow-y: hidden;
}

.teacher-header.has-school-logo {
  background: #307CF4;
}

.header-left {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  width: 280px;
}

.logo {
  display: flex;
  align-items: center;
  gap: 10px;
}

.logo-img {
  margin-top: 20px;
  margin-left: -10px;
  height: 4rem;
  width: 14rem;
  min-width: 14rem;
  min-height: 4rem;
  border: 2px solid #ffffff;
  border-radius: 8px;
  object-fit: contain;
  background-color: #ffffff;
}

.logo-text {
  font-size: 18px;
  font-weight: bold;
  color: #409eff;
}

.header-center {
  flex: 1;
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-top: 20px;
  min-width: 700px;
  overflow: visible;
}

.header-menu {
  border-bottom: none;
  background-color: #307CF4;
  gap: 1rem;
}

.header-right {
  display: flex;
  align-items: center;
  margin-top: 20px;
  flex-shrink: 0;
  width: 200px;
  justify-content: flex-end;
}

/* 取消底部横线 */
:deep(.el-menu--horizontal) {
  border-bottom: none !important;
}

/* 深度选择器穿透,改变标题字体大小 */
:deep(.el-menu-item) {
  font-size: 16px;
  white-space: nowrap;
  overflow: visible;
  text-overflow: unset;
  min-width: auto;
  flex-shrink: 0;
}


.user-info {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 6px;
  transition: background-color 0.3s;
}

.user-info:hover {
  background-color: #f5f7fa;
}

.user-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #fff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.username {
  font-size: 14px;
  color: #fff;
  font-weight: 500;
}

@media (max-width: 1300px) {
  .header-left {
    width: 200px;
  }
  
  .logo-img {
    height: 2.5rem;
    width: 8rem;
  }
  
  .header-right {
    width: 150px;
  }
  
  .header-center {
    min-width: 600px;
  }
}

@media (max-width: 768px) {
  .header-left {
    width: 120px;
  }
  
  .logo-img {
    height: 2rem;
    width: 6rem;
  }
  
  .header-right {
    width: 100px;
  }
  
  .username {
    display: none;
  }
}

/* 强制Element Plus菜单在大屏幕下显示完整菜单 */
:deep(.el-menu--horizontal .el-menu-item) {
  display: flex !important;
}

:deep(.el-menu--horizontal .el-sub-menu) {
  display: flex !important;
}

/* 确保菜单项在足够宽度时始终可见 */
@media (min-width: 769px) {
  :deep(.el-menu--horizontal .el-menu-item),
  :deep(.el-menu--horizontal .el-sub-menu) {
    display: flex !important;
    visibility: visible !important;
  }
  
  :deep(.el-menu--horizontal .el-menu-item.is-hidden),
  :deep(.el-menu--horizontal .el-sub-menu.is-hidden) {
    display: flex !important;
    visibility: visible !important;
  }
}
</style>
